import { useQueryClient } from '@tanstack/react-query'
import {
	CHECKUP_COMMENT_MAX_LENGTH,
	isCheckupValid,
} from 'components/cattles/checkupPage/utils/checkupValidation'
import { getCheckupScheduleFromDiagnosis } from 'components/cattles/treatmentPage/utils/treatmentSchemaDisplay'
import { useGetTreatmentSchemas } from 'hook/query/useGetTreatmentSchemas'
import { ICheckupList } from 'models'
import { ICowsList } from 'models/ICattlesData'
import { useNavigate } from 'react-router-dom'
import CattlesApiService from 'services/cattlesApiServices'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { notifyUser } from 'store/notificationsSlice'
import {
	leaveCheckupPage,
	broadcastCheckupSavedNotice,
} from 'utils/checkupWindow'

interface SubmitCheckUpInfoProps {
	checkupInfo: ICheckupList
	cowInfo: ICowsList
}

export const useSubmitCheckUpInfo = ({
	checkupInfo,
	cowInfo,
}: SubmitCheckUpInfoProps) => {
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const queryClient = useQueryClient()
	const { userData } = useAppSelector(state => state.userReducer)
	const { data: treatmentSchemas = [] } = useGetTreatmentSchemas()

	const submitForm = () => {
		if (!isCheckupValid(checkupInfo)) return

		const promises = []
		const schedule = getCheckupScheduleFromDiagnosis(
			checkupInfo.diagnosis.flatMap(item => item.treatment_plans_id || []),
			treatmentSchemas,
		)
		const updateData = {
			next_examination_date: schedule.nextExaminationDate,
			milk_reject_end: schedule.milkRejectEnd,
			milk_reject_start: schedule.milkRejectStart,
			dispose_recommended: checkupInfo.dispose_recommended,
			comment: (checkupInfo.comment ?? '').slice(0, CHECKUP_COMMENT_MAX_LENGTH),
			diagnosis: checkupInfo.diagnosis.map(el => ({
				position: el.position,
				sickness_id:
					el.sickness?.map(sickness => sickness.id) || el.sickness_id || [],
				treatment_plans_id: el.treatment_plans_id || [],
				comment: el.comment ?? '',
			})),
		}

		promises.push(
			CattlesApiService.postCheckupCowInfoByCowId(cowInfo.id, updateData),
		)
		if (
			checkupInfo.trim.isTrim &&
			location.pathname !== '/cattles/trim/check-up'
		) {
			promises.push(
				CattlesApiService.postTrimByCowId(
					cowInfo.id,
					checkupInfo.trim.target_date,
					userData.id,
					checkupInfo.trim.comment,
				),
			)
		}
		Promise.all(promises)
			.then(() => {
				queryClient.invalidateQueries({ queryKey: ['cowsList'] })
				queryClient.invalidateQueries({ queryKey: ['cowsTabCount'] })
				dispatch(
					notifyUser({
						title: 'Данные успешно сохранены',
						description: '',
						isError: false,
					}),
				)
				broadcastCheckupSavedNotice({
					title: 'Данные успешно сохранены',
					description: '',
					isError: false,
				})
				leaveCheckupPage(navigate)
			})
			.catch(error => {
				dispatch(
					notifyUser({
						title: `Осмотр коровы ${cowInfo.tag_id} завершён с ошибкой.`,
						description: error.message,
						isError: true,
					}),
				)
			})
	}
	return { submitForm }
}
